import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import type { NavPage } from '../lib/api';
import { isStaff } from '../lib/api';
import { useAuth } from '../lib/auth';

/** Menu order is fixed by the site structure, not by whatever the API returns first. */
const GROUP_ORDER = ['About Us', 'Join the Training', 'Life in the Training'];

interface NavGroup {
  label: string;
  pages: NavPage[];
}

function buildGroups(pages: NavPage[]): { groups: NavGroup[]; loose: NavPage[] } {
  const byGroup = new Map<string, NavPage[]>();
  const loose: NavPage[] = [];

  for (const page of pages) {
    if (!page.navGroup) {
      loose.push(page);
      continue;
    }
    const list = byGroup.get(page.navGroup) ?? [];
    list.push(page);
    byGroup.set(page.navGroup, list);
  }

  const known = GROUP_ORDER.filter((label) => byGroup.has(label)).map((label) => ({
    label,
    pages: byGroup.get(label)!,
  }));
  // Any group added later still shows up, just after the known ones.
  const extra = [...byGroup.keys()]
    .filter((label) => !GROUP_ORDER.includes(label))
    .map((label) => ({ label, pages: byGroup.get(label)! }));

  return { groups: [...known, ...extra], loose };
}

const chevron = (
  <svg aria-hidden viewBox="0 0 20 20" className="h-3.5 w-3.5 opacity-60" fill="currentColor">
    <path fillRule="evenodd" d="M5.2 7.5 10 12.3l4.8-4.8 1.2 1.2-6 6-6-6 1.2-1.2Z" clipRule="evenodd" />
  </svg>
);

const authIcon = (direction: 'in' | 'out') => (
  <svg
    aria-hidden
    viewBox="0 0 24 24"
    className="h-5 w-5 shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {direction === 'in' ? (
      <>
        <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
        <path d="M10 8.5 13.5 12 10 15.5M13.5 12H4" />
      </>
    ) : (
      <>
        <path d="M9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3" />
        <path d="M16 8.5 19.5 12 16 15.5M19.5 12H10" />
      </>
    )}
  </svg>
);

const signInIcon = authIcon('in');
const signOutIcon = authIcon('out');

export default function Header({ pages }: { pages: NavPage[] }) {
  const { groups, loose } = buildGroups(pages);
  const { user, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  useEffect(() => {
    setMobileOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!openGroup) return;
    const onPointerDown = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenGroup(null);
    };
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && setOpenGroup(null);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openGroup]);

  /*
    Hover opens the dropdown on desktop. Closing is delayed slightly so a pointer
    travelling diagonally from the trigger into the menu does not dismiss it.
  */
  const openOnHover = (label: string) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpenGroup(label);
  };

  const closeOnLeave = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenGroup(null), 120);
  };

  /*
    Already on an account screen. Offering "Sign in" there points at the page
    being looked at, so the control — and the divider before it — come off.
  */
  const onAuthPage = /^\/(login|register|forgot-password|reset-password|account-pending)/.test(
    location.pathname,
  );

  /* Every nav item is nowrap — a wrapped label makes the whole bar grow and look broken. */
  const itemBase =
    'flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-[0.9375rem] font-medium transition-colors';

  return (
    <>
    <header className="sticky top-0 z-50 border-b border-ink-100 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full items-center gap-3 px-4 sm:h-18 sm:gap-6 sm:px-6 lg:px-10 xl:px-14">
        {/* ------------------------------- brand ------------------------------- */}
        {/* Logo only — drop your file at client/public/logo.png, served from /logo.png. */}
        <Link to="/" className="flex min-w-0 items-center">
          <img
            src="/logo.png"
            alt="Full Time Training in Malaysia"
            className="h-9 max-w-[58vw] w-auto object-contain sm:h-11 sm:max-w-none"
          />
        </Link>

        {/* ---------------------------- desktop nav ---------------------------- */}
        <nav ref={navRef} className="ml-auto hidden items-center gap-0.5 lg:flex" aria-label="Main">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `${itemBase} ${isActive ? 'text-gold-600' : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900'}`
            }
          >
            Home
          </NavLink>

          {groups.map((group) => {
            const open = openGroup === group.label;
            const active = group.pages.some((p) => location.pathname === `/${p.slug}`);
            return (
              <div
                key={group.label}
                className="relative"
                onMouseEnter={() => openOnHover(group.label)}
                onMouseLeave={closeOnLeave}
              >
                <button
                  type="button"
                  // Click still toggles, so touch devices and keyboards are not stranded.
                  onClick={() => setOpenGroup(open ? null : group.label)}
                  onFocus={() => setOpenGroup(group.label)}
                  aria-expanded={open}
                  aria-haspopup="true"
                  className={`${itemBase} ${
                    active || open ? 'text-gold-600' : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900'
                  }`}
                >
                  {group.label}
                  <span className={`transition-transform ${open ? 'rotate-180' : ''}`}>{chevron}</span>
                </button>

                {open && (
                  // pt-2 on the wrapper keeps the hover path unbroken — a margin here
                  // would leave a dead gap that closes the menu as the pointer crosses it.
                  <div className="absolute right-0 top-full pt-2">
                    <div className="min-w-64 rounded-xl border border-ink-100 bg-white p-1.5 shadow-xl shadow-ink-950/8">
                      {group.pages.map((page) => (
                        <NavLink
                          key={page.slug}
                          to={`/${page.slug}`}
                          className={({ isActive }) =>
                            `block whitespace-nowrap rounded-lg px-3 py-2 text-[0.9375rem] transition-colors ${
                              isActive
                                ? 'bg-gold-100 font-medium text-gold-600'
                                : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900'
                            }`
                          }
                        >
                          {page.title}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {loose.map((page) => (
            <NavLink
              key={page.slug}
              to={`/${page.slug}`}
              className={({ isActive }) =>
                `${itemBase} ${isActive ? 'text-gold-600' : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900'}`
              }
            >
              {page.title}
            </NavLink>
          ))}

          {(user || !onAuthPage) && (
            <span aria-hidden className="mx-3 h-6 w-px shrink-0 bg-ink-200" />
          )}

          {/* Icon only on desktop; the mobile drawer keeps the words, where
              there is room and a bare glyph would be less obvious. */}
          {!user ? (
            !onAuthPage && (
              <Link
                to="/login"
                aria-label="Sign in"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-ink-200 text-ink-600 transition-colors hover:border-ink-300 hover:bg-ink-50 hover:text-ink-900"
              >
                {signInIcon}
              </Link>
            )
          ) : (
            <div className="flex shrink-0 items-center gap-1.5">
              {isStaff(user.role) && (
                <Link
                  to="/admin"
                  className={`${itemBase} text-ink-600 hover:bg-ink-50 hover:text-ink-900`}
                >
                  Admin
                </Link>
              )}
              <button
                type="button"
                onClick={() => void signOut()}
                aria-label="Sign out"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-ink-200 text-ink-600 transition-colors hover:border-ink-300 hover:bg-red-50 hover:text-red-700"
              >
                {signOutIcon}
              </button>
            </div>
          )}
        </nav>

        {/* --------------------------- mobile trigger -------------------------- */}
        <button
          type="button"
          onClick={() => setMobileOpen((open) => !open)}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          className="-mr-2 ml-auto grid h-11 w-11 shrink-0 place-items-center rounded-lg text-ink-900 transition-colors hover:bg-ink-50 lg:hidden"
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.75">
            {mobileOpen ? (
              <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            ) : (
              <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
            )}
          </svg>
        </button>
      </div>
      </header>

      {/*
        Rendered OUTSIDE <header> on purpose. The header carries backdrop-blur,
        and a non-none backdrop-filter makes an element a containing block for
        its position:fixed descendants — so nested here, top-16/bottom-0 would
        resolve against the 64px header box instead of the viewport and the
        drawer would collapse to nothing.
      */}
      {mobileOpen && (
        <div
          id="mobile-menu"
          className="fixed inset-x-0 bottom-0 top-16 z-40 overflow-y-auto overscroll-contain border-t border-ink-100 bg-white px-4 pb-10 pt-3 sm:top-18 sm:px-6 lg:hidden"
        >
          <nav aria-label="Main" className="flex flex-col">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `rounded-lg px-3 py-3 text-base font-medium ${
                  isActive ? 'bg-gold-100 text-gold-600' : 'text-ink-800 hover:bg-ink-50'
                }`
              }
            >
              Home
            </NavLink>

            {groups.map((group) => (
              <div key={group.label} className="mt-2 border-t border-ink-100 pt-2">
                <p className="px-3 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-ink-400">
                  {group.label}
                </p>
                {group.pages.map((page) => (
                  <NavLink
                    key={page.slug}
                    to={`/${page.slug}`}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-2.5 text-base ${
                        isActive ? 'bg-gold-100 font-medium text-gold-600' : 'text-ink-900 hover:bg-ink-50'
                      }`
                    }
                  >
                    {page.title}
                  </NavLink>
                ))}
              </div>
            ))}

            {loose.length > 0 && (
              <div className="mt-2 border-t border-ink-100 pt-2">
                {loose.map((page) => (
                  <NavLink
                    key={page.slug}
                    to={`/${page.slug}`}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-3 text-base font-medium ${
                        isActive ? 'bg-gold-100 text-gold-600' : 'text-ink-800 hover:bg-ink-50'
                      }`
                    }
                  >
                    {page.title}
                  </NavLink>
                ))}
              </div>
            )}

            {!user ? (
              !onAuthPage && (
              <div className="mt-5 border-t border-ink-100 pt-5">
                <Link
                  to="/login"
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-ink-200 px-4 py-3 text-base font-medium text-ink-800 transition-colors hover:bg-ink-50"
                >
                  {signInIcon}
                  Sign in
                </Link>
              </div>
              )
            ) : (
              <div className="mt-5 border-t border-ink-100 pt-5">
                <div className="space-y-2">
                  <p className="px-3 text-sm text-ink-500">
                    Signed in as <span className="font-medium text-ink-800">{user.fullName}</span>
                  </p>
                  {isStaff(user.role) && (
                    <Link
                      to="/admin"
                      className="block rounded-lg px-3 py-3 text-base font-medium text-ink-800 hover:bg-ink-50"
                    >
                      Admin
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => void signOut()}
                    className="w-full rounded-lg border border-ink-200 px-4 py-3 text-base font-medium text-ink-900"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </nav>
        </div>
      )}
    </>
  );
}
